import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type BusinessOperationTraceV1,
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import {
  type PurchaseOrderSupplierSnapshot,
  PurchaseOrderSupplierSnapshotSchema,
} from "./procurement.js";
import { PurchaseOrderNumberSchema } from "./purchase-order-number.js";
import {
  allocatePurchaseReceiptFen,
  isPurchaseInventoryValuationSnapshot,
  isPurchaseReceiptRecord,
  type PurchaseInventoryValuationSnapshot,
  PurchaseInventoryValuationSnapshotSchema,
  type PurchaseReceiptRecord,
  PurchaseReceiptRecordSchema,
} from "./purchase-order-receiving.js";
import { PurchaseReceiptNumberSchema } from "./purchase-receipt-number.js";
import {
  derivePurchaseReturnNumber,
  PurchaseReturnNumberSchema,
} from "./purchase-return-number.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const LineIdSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$",
});
const CanonicalTextPattern = "^(?:\\S|\\S[\\s\\S]*\\S)$";
const CanonicalTextSchema = (maximum: number) =>
  Type.String({ minLength: 1, maxLength: maximum, pattern: CanonicalTextPattern });
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({ minLength: 20, maxLength: 64 });
const QuantitySchema = Type.Integer({ minimum: 1, maximum: 1_000_000_000_000 });
const NonNegativeQuantitySchema = Type.Integer({ minimum: 0, maximum: 1_000_000_000_000 });
const AmountFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const SignedAmountFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const IdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$",
});

export const PurchaseReturnLineRequestSchema = Type.Object(
  {
    receiptLineId: LineIdSchema,
    returnedBaseQuantity: QuantitySchema,
  },
  { additionalProperties: false },
);
export type PurchaseReturnLineRequest = Static<typeof PurchaseReturnLineRequestSchema>;

export const RecordPurchaseReturnRequestSchema = Type.Object(
  {
    returnId: StableIdSchema,
    receiptId: StableIdSchema,
    businessDate: BusinessDateSchema,
    reason: CanonicalTextSchema(200),
    lines: Type.Array(PurchaseReturnLineRequestSchema, { minItems: 1, maxItems: 200 }),
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RecordPurchaseReturnRequest = Static<typeof RecordPurchaseReturnRequestSchema>;

export const PurchaseReturnLineRecordSchema = Type.Object(
  {
    receiptLineId: LineIdSchema,
    movementId: StableIdSchema,
    skuId: LineIdSchema,
    productName: CanonicalTextSchema(80),
    productVersion: VersionSchema,
    baseUnitName: CanonicalTextSchema(16),
    sourceReceivedBaseQuantity: QuantitySchema,
    sourceReceiptSubtotalFen: AmountFenSchema,
    sourceReceiptTaxFen: AmountFenSchema,
    sourceReceiptTotalFen: AmountFenSchema,
    returnedBaseQuantityBefore: NonNegativeQuantitySchema,
    returnedBaseQuantity: QuantitySchema,
    returnedBaseQuantityAfter: QuantitySchema,
    returnableBaseQuantityAfter: NonNegativeQuantitySchema,
    payableSubtotalDecreaseFen: AmountFenSchema,
    payableTaxDecreaseFen: AmountFenSchema,
    payableTotalDecreaseFen: AmountFenSchema,
    inventoryQuantityBefore: QuantitySchema,
    inventoryQuantityAfter: NonNegativeQuantitySchema,
    inventoryVersionBefore: VersionSchema,
    inventoryVersionAfter: VersionSchema,
    valuationBefore: PurchaseInventoryValuationSnapshotSchema,
    valuationAfter: PurchaseInventoryValuationSnapshotSchema,
    inventoryValueDecreaseFen: AmountFenSchema,
    purchasePriceVarianceFen: SignedAmountFenSchema,
    valuationAfterSource: Type.Object(
      {
        kind: Type.Literal("PURCHASE_RETURN"),
        returnId: StableIdSchema,
      },
      { additionalProperties: false },
    ),
  },
  { additionalProperties: false },
);
export type PurchaseReturnLineRecord = Static<typeof PurchaseReturnLineRecordSchema>;

export const PurchaseReturnRecordSchema = Type.Object(
  {
    returnId: StableIdSchema,
    returnNumber: PurchaseReturnNumberSchema,
    receiptId: StableIdSchema,
    receiptNumber: PurchaseReceiptNumberSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    supplier: PurchaseOrderSupplierSnapshotSchema,
    businessDate: BusinessDateSchema,
    reason: CanonicalTextSchema(200),
    lines: Type.Array(PurchaseReturnLineRecordSchema, { minItems: 1, maxItems: 200 }),
    payableSubtotalDecreaseFen: AmountFenSchema,
    payableTaxDecreaseFen: AmountFenSchema,
    payableTotalDecreaseFen: AmountFenSchema,
    inventoryValueDecreaseFen: AmountFenSchema,
    purchasePriceVarianceFen: SignedAmountFenSchema,
    payableOutstandingFenBefore: AmountFenSchema,
    payableOutstandingFenAfter: AmountFenSchema,
    returnerActorId: StableIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    returnedAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type PurchaseReturnRecord = Static<typeof PurchaseReturnRecordSchema>;

export const PurchaseReturnProjectionStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("SYNCED"),
  Type.Literal("NEEDS_REVIEW"),
]);
export const PurchaseReturnWriteResultSchema = Type.Object(
  {
    purchaseReturn: PurchaseReturnRecordSchema,
    cloudProjectionStatus: PurchaseReturnProjectionStatusSchema,
  },
  { additionalProperties: false },
);
export type PurchaseReturnWriteResult = Static<typeof PurchaseReturnWriteResultSchema>;

export const PurchaseReturnWorkspaceLineSchema = Type.Object(
  {
    receiptLineId: LineIdSchema,
    skuId: LineIdSchema,
    productName: CanonicalTextSchema(80),
    productVersion: VersionSchema,
    baseUnitName: CanonicalTextSchema(16),
    receivedBaseQuantity: QuantitySchema,
    returnedBaseQuantity: NonNegativeQuantitySchema,
    returnableBaseQuantity: NonNegativeQuantitySchema,
    payableSubtotalFen: AmountFenSchema,
    payableTaxFen: AmountFenSchema,
    payableTotalFen: AmountFenSchema,
    inventoryQuantity: NonNegativeQuantitySchema,
    inventoryVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    valuation: PurchaseInventoryValuationSnapshotSchema,
  },
  { additionalProperties: false },
);

export const PurchaseReturnWorkspaceSchema = Type.Object(
  {
    receipt: PurchaseReceiptRecordSchema,
    lines: Type.Array(PurchaseReturnWorkspaceLineSchema, { minItems: 1, maxItems: 200 }),
    returns: Type.Array(PurchaseReturnWriteResultSchema, { maxItems: 500 }),
    payableOutstandingFen: AmountFenSchema,
    lastCommandId: StableIdSchema,
    canReturn: Type.Boolean(),
    blocker: Type.Union([CanonicalTextSchema(300), Type.Null()]),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type PurchaseReturnWorkspace = Static<typeof PurchaseReturnWorkspaceSchema>;

export const PurchaseReturnBusinessEventPayloadSchema = Type.Object(
  { purchaseReturn: PurchaseReturnRecordSchema },
  { additionalProperties: false },
);
export type PurchaseReturnBusinessEventPayload = Static<
  typeof PurchaseReturnBusinessEventPayloadSchema
>;

function isCanonicalBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function safeSum(values: readonly number[]): number | null {
  let total = 0;
  for (const value of values) {
    if (!Number.isSafeInteger(value)) return null;
    total += value;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

export function allocateMovingAverageOutboundFen(
  totalValueFen: number,
  inventoryQuantity: number,
  outboundQuantity: number,
): number {
  if (
    !Number.isSafeInteger(totalValueFen) ||
    totalValueFen < 0 ||
    !Number.isSafeInteger(inventoryQuantity) ||
    inventoryQuantity <= 0 ||
    !Number.isSafeInteger(outboundQuantity) ||
    outboundQuantity <= 0 ||
    outboundQuantity > inventoryQuantity
  ) {
    throw new RangeError("moving-average outbound inputs are outside the safe range");
  }
  if (outboundQuantity === inventoryQuantity) return totalValueFen;
  return Number((BigInt(totalValueFen) * BigInt(outboundQuantity)) / BigInt(inventoryQuantity));
}

function traceMatchesReturn(
  trace: BusinessOperationTraceV1,
  purchaseOrderId: string,
  purchaseOrderNumber: string,
  idempotencyKey: string,
): boolean {
  return (
    isBusinessOperationTraceV1(trace) &&
    trace.commandId === idempotencyKey &&
    trace.idempotencyKey === idempotencyKey &&
    trace.correlationId === `purchase-order:${purchaseOrderId}` &&
    trace.causationId !== null &&
    trace.businessNumber?.kind === "PURCHASE_ORDER" &&
    trace.businessNumber.value === purchaseOrderNumber
  );
}

export function isRecordPurchaseReturnRequest(
  value: unknown,
): value is RecordPurchaseReturnRequest {
  if (!Value.Check(RecordPurchaseReturnRequestSchema, value)) return false;
  const request = value as RecordPurchaseReturnRequest;
  return (
    isCanonicalBusinessDate(request.businessDate) &&
    request.reason === request.reason.trim() &&
    request.operationTrace.commandId === request.idempotencyKey &&
    request.operationTrace.idempotencyKey === request.idempotencyKey &&
    request.operationTrace.causationId !== null &&
    new Set(request.lines.map((line) => line.receiptLineId)).size === request.lines.length
  );
}

export function isPurchaseReturnWorkspace(value: unknown): value is PurchaseReturnWorkspace {
  if (!Value.Check(PurchaseReturnWorkspaceSchema, value)) return false;
  const workspace = value as PurchaseReturnWorkspace;
  if (!isPurchaseReceiptRecord(workspace.receipt) || !isCanonicalTimestamp(workspace.generatedAt)) {
    return false;
  }
  const sourceById = new Map(workspace.receipt.lines.map((line) => [line.lineId, line]));
  const workspaceLineIds = workspace.lines.map((line) => line.receiptLineId);
  const returnIds = workspace.returns.map((result) => result.purchaseReturn.returnId);
  if (
    workspace.lines.length !== workspace.receipt.lines.length ||
    new Set(workspaceLineIds).size !== workspaceLineIds.length ||
    new Set(returnIds).size !== returnIds.length ||
    workspace.payableOutstandingFen > workspace.receipt.totalFen ||
    !workspace.returns.every(
      (result) =>
        isPurchaseReturnWriteResult(result) &&
        result.purchaseReturn.receiptId === workspace.receipt.receiptId,
    )
  ) {
    return false;
  }
  return workspace.lines.every((line) => {
    const source = sourceById.get(line.receiptLineId);
    return (
      source !== undefined &&
      line.skuId === source.skuId &&
      line.productName === source.productName &&
      line.productVersion === source.productVersion &&
      line.baseUnitName === source.baseUnitName &&
      line.receivedBaseQuantity === source.receivedBaseQuantity &&
      line.returnedBaseQuantity + line.returnableBaseQuantity === line.receivedBaseQuantity &&
      line.payableSubtotalFen === source.subtotalFen &&
      line.payableTaxFen === source.taxFen &&
      line.payableTotalFen === source.totalFen &&
      isPurchaseInventoryValuationSnapshot(line.valuation) &&
      line.valuation.quantity === line.inventoryQuantity
    );
  });
}

export function isRecordPurchaseReturnAgainstWorkspace(
  requestValue: unknown,
  workspaceValue: unknown,
): requestValue is RecordPurchaseReturnRequest {
  if (!isRecordPurchaseReturnRequest(requestValue) || !isPurchaseReturnWorkspace(workspaceValue)) {
    return false;
  }
  const request = requestValue as RecordPurchaseReturnRequest;
  const workspace = workspaceValue as PurchaseReturnWorkspace;
  if (
    request.receiptId !== workspace.receipt.receiptId ||
    request.businessDate < workspace.receipt.businessDate ||
    !workspace.canReturn ||
    request.operationTrace.causationId !== workspace.lastCommandId ||
    !traceMatchesReturn(
      request.operationTrace,
      workspace.receipt.purchaseOrderId,
      workspace.receipt.purchaseOrderNumber,
      request.idempotencyKey,
    )
  ) {
    return false;
  }
  const lineById = new Map(workspace.lines.map((line) => [line.receiptLineId, line]));
  return request.lines.every((line) => {
    const current = lineById.get(line.receiptLineId);
    return (
      current !== undefined &&
      line.returnedBaseQuantity <= current.returnableBaseQuantity &&
      line.returnedBaseQuantity <= current.inventoryQuantity
    );
  });
}

function isPurchaseReturnLineRecord(value: unknown): value is PurchaseReturnLineRecord {
  if (!Value.Check(PurchaseReturnLineRecordSchema, value)) return false;
  const line = value as PurchaseReturnLineRecord;
  if (
    line.returnedBaseQuantityAfter !==
      line.returnedBaseQuantityBefore + line.returnedBaseQuantity ||
    line.returnedBaseQuantityAfter > line.sourceReceivedBaseQuantity ||
    line.returnableBaseQuantityAfter !==
      line.sourceReceivedBaseQuantity - line.returnedBaseQuantityAfter ||
    line.sourceReceiptTotalFen !== line.sourceReceiptSubtotalFen + line.sourceReceiptTaxFen ||
    line.payableTotalDecreaseFen !== line.payableSubtotalDecreaseFen + line.payableTaxDecreaseFen ||
    line.inventoryQuantityAfter !== line.inventoryQuantityBefore - line.returnedBaseQuantity ||
    line.inventoryVersionAfter !== line.inventoryVersionBefore + 1 ||
    !isPurchaseInventoryValuationSnapshot(line.valuationBefore) ||
    !isPurchaseInventoryValuationSnapshot(line.valuationAfter) ||
    line.valuationBefore.quantity !== line.inventoryQuantityBefore ||
    line.valuationAfter.quantity !== line.inventoryQuantityAfter ||
    line.inventoryValueDecreaseFen !==
      line.valuationBefore.totalValueFen - line.valuationAfter.totalValueFen ||
    line.purchasePriceVarianceFen !==
      line.payableSubtotalDecreaseFen - line.inventoryValueDecreaseFen ||
    line.valuationAfterSource.returnId.length === 0
  ) {
    return false;
  }
  let expectedInventoryDecrease: number;
  try {
    expectedInventoryDecrease = allocateMovingAverageOutboundFen(
      line.valuationBefore.totalValueFen,
      line.inventoryQuantityBefore,
      line.returnedBaseQuantity,
    );
  } catch {
    return false;
  }
  let expectedSubtotalDecrease: number;
  let expectedTaxDecrease: number;
  try {
    expectedSubtotalDecrease = payableDecreaseForPurchaseReturnLine(
      line.sourceReceiptSubtotalFen,
      line.sourceReceivedBaseQuantity,
      line.returnedBaseQuantityBefore,
      line.returnedBaseQuantityAfter,
    );
    expectedTaxDecrease = payableDecreaseForPurchaseReturnLine(
      line.sourceReceiptTaxFen,
      line.sourceReceivedBaseQuantity,
      line.returnedBaseQuantityBefore,
      line.returnedBaseQuantityAfter,
    );
  } catch {
    return false;
  }
  return (
    expectedInventoryDecrease === line.inventoryValueDecreaseFen &&
    expectedSubtotalDecrease === line.payableSubtotalDecreaseFen &&
    expectedTaxDecrease === line.payableTaxDecreaseFen
  );
}

export function isPurchaseReturnRecord(value: unknown): value is PurchaseReturnRecord {
  if (!Value.Check(PurchaseReturnRecordSchema, value)) return false;
  const purchaseReturn = value as PurchaseReturnRecord;
  let expectedNumber: string;
  try {
    expectedNumber = derivePurchaseReturnNumber(
      purchaseReturn.businessDate,
      purchaseReturn.returnId,
    );
  } catch {
    return false;
  }
  if (
    purchaseReturn.returnNumber !== expectedNumber ||
    !isCanonicalBusinessDate(purchaseReturn.businessDate) ||
    !isCanonicalTimestamp(purchaseReturn.returnedAt) ||
    purchaseReturn.reason !== purchaseReturn.reason.trim() ||
    !traceMatchesReturn(
      purchaseReturn.operationTrace,
      purchaseReturn.purchaseOrderId,
      purchaseReturn.purchaseOrderNumber,
      purchaseReturn.operationTrace.idempotencyKey,
    ) ||
    purchaseReturn.returnerActorId.length === 0 ||
    new Set(purchaseReturn.lines.map((line) => line.receiptLineId)).size !==
      purchaseReturn.lines.length ||
    !purchaseReturn.lines.every(
      (line) =>
        isPurchaseReturnLineRecord(line) &&
        line.valuationAfterSource.returnId === purchaseReturn.returnId,
    )
  ) {
    return false;
  }
  const subtotal = safeSum(purchaseReturn.lines.map((line) => line.payableSubtotalDecreaseFen));
  const tax = safeSum(purchaseReturn.lines.map((line) => line.payableTaxDecreaseFen));
  const total = safeSum(purchaseReturn.lines.map((line) => line.payableTotalDecreaseFen));
  const inventory = safeSum(purchaseReturn.lines.map((line) => line.inventoryValueDecreaseFen));
  const variance = safeSum(purchaseReturn.lines.map((line) => line.purchasePriceVarianceFen));
  return (
    subtotal !== null &&
    tax !== null &&
    total !== null &&
    inventory !== null &&
    variance !== null &&
    subtotal === purchaseReturn.payableSubtotalDecreaseFen &&
    tax === purchaseReturn.payableTaxDecreaseFen &&
    total === purchaseReturn.payableTotalDecreaseFen &&
    total === subtotal + tax &&
    inventory === purchaseReturn.inventoryValueDecreaseFen &&
    variance === purchaseReturn.purchasePriceVarianceFen &&
    purchaseReturn.payableOutstandingFenAfter ===
      purchaseReturn.payableOutstandingFenBefore - purchaseReturn.payableTotalDecreaseFen
  );
}

export function isPurchaseReturnWriteResult(value: unknown): value is PurchaseReturnWriteResult {
  return (
    Value.Check(PurchaseReturnWriteResultSchema, value) &&
    isPurchaseReturnRecord((value as PurchaseReturnWriteResult).purchaseReturn)
  );
}

export function isPurchaseReturnBusinessEventPayload(
  value: unknown,
): value is PurchaseReturnBusinessEventPayload {
  return (
    Value.Check(PurchaseReturnBusinessEventPayloadSchema, value) &&
    isPurchaseReturnRecord((value as PurchaseReturnBusinessEventPayload).purchaseReturn)
  );
}

export function payableDecreaseForPurchaseReturnLine(
  sourceAmountFen: number,
  sourceReceivedBaseQuantity: number,
  returnedBaseQuantityBefore: number,
  returnedBaseQuantityAfter: number,
): number {
  return allocatePurchaseReceiptFen(
    sourceAmountFen,
    sourceReceivedBaseQuantity,
    returnedBaseQuantityBefore,
    returnedBaseQuantityAfter,
  );
}

export type {
  PurchaseInventoryValuationSnapshot,
  PurchaseOrderSupplierSnapshot,
  PurchaseReceiptRecord,
};
